// ═══════════════════════════════════════════════════════════════════
// ONBOARDING TOUR — Guided tooltips for first login
// ═══════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef, useMemo, type CSSProperties } from "react";
import { type ThemeObject, GLASS, TRANSITION, SP, RADIUS } from "../shared/theme.js";
import { useIsMobile } from "../shared/hooks.js";
import { subscribeToPush, isPushSupported } from "../services/pushService.js";

interface TourStep {
  id: string;
  title: string;
  description: string;
  target: string | null;
  position: "center" | "top" | "bottom" | "left" | "right";
  desktopOnly?: boolean;
  mobileOnly?: boolean;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface OnboardingTourProps {
  T: ThemeObject;
  onComplete?: () => void;
}

const ONBOARDING_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: "Bem-vindo ao LPA Wealth",
    description: "Vamos fazer um rápido tour pelo seu portal de investimentos.",
    target: null,
    position: "center",
  },
  {
    id: "sidebar",
    title: "Navegação",
    description:
      "O menu lateral organiza todas as seções em grupos. Clique em um grupo para expandir suas páginas.",
    target: ".lpa-sidebar",
    position: "right",
    desktopOnly: true,
  },
  {
    id: "nav-investimentos",
    title: "Investimentos",
    description:
      "Aqui você encontra Carteira, Performance, Métricas e Dividendos — tudo sobre seus ativos.",
    target: "[data-tour='nav-group-investimentos']",
    position: "right",
    desktopOnly: true,
  },
  {
    id: "nav-operacoes",
    title: "Operações",
    description:
      "Movimentações, Aportes e Relatórios — acompanhe cada operação e gere extratos detalhados.",
    target: "[data-tour='nav-group-operacoes']",
    position: "right",
    desktopOnly: true,
  },
  {
    id: "nav-planejamento",
    title: "Planejamento",
    description:
      "Cenários, Metas, Aposentadoria e Sucessão Patrimonial — planeje o futuro do seu patrimônio.",
    target: "[data-tour='nav-group-planejamento']",
    position: "right",
    desktopOnly: true,
  },
  {
    id: "nav-fiscal",
    title: "Fiscal",
    description: "Calculadora de IR e ferramentas tributárias para otimizar sua carga fiscal.",
    target: "[data-tour='nav-group-fiscal']",
    position: "right",
    desktopOnly: true,
  },
  {
    id: "nav-recursos",
    title: "Recursos",
    description:
      "Documentos importantes e conteúdo educacional para aprimorar seus conhecimentos financeiros.",
    target: "[data-tour='nav-group-recursos']",
    position: "right",
    desktopOnly: true,
  },
  {
    id: "dashboard-kpis",
    title: "Indicadores Principais",
    description: "Veja seus KPIs e patrimônio total em um relance.",
    target: "[data-tour='dashboard-kpis']",
    position: "bottom",
  },
  {
    id: "period-selector",
    title: "Seletor de Período",
    description: "Escolha qual período você deseja visualizar. Clique para ver histórico.",
    target: "[data-tour='period-selector']",
    position: "bottom",
  },
  {
    id: "dark-mode",
    title: "Modo Escuro",
    description: "Alterne entre modo claro e escuro de acordo com sua preferência.",
    target: "[data-tour='dark-mode-toggle']",
    position: "left",
  },
  {
    id: "profile",
    title: "Seu Perfil",
    description: "Acesse suas configurações, informações pessoais e preferências.",
    target: "[data-tour='profile-button']",
    position: "left",
  },
  {
    id: "push-notifications",
    title: "Notificações Push",
    description:
      "Receba alertas em tempo real sobre seus investimentos: novos relatórios, mudanças de carteira e lembretes importantes.",
    target: null,
    position: "center",
  },
  {
    id: "mobile-hint",
    title: "Acesso Mobile",
    description:
      "No celular, use a barra inferior para navegar rapidamente e deslize para trocar de página.",
    target: "[data-tour='mobile-tab-bar']",
    position: "top",
    mobileOnly: true,
  },
  {
    id: "complete",
    title: "Pronto! 🎉",
    description: "Você está pronto para explorar seu portal. Aproveite!",
    target: null,
    position: "center",
  },
];

export default function OnboardingTour({ T, onComplete }: OnboardingTourProps) {
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const steps = useMemo(
    () =>
      ONBOARDING_STEPS.filter((s) => {
        if (s.mobileOnly) return isMobile;
        if (s.desktopOnly) return !isMobile;
        if (s.id === "push-notifications") return isPushSupported();
        return true;
      }),
    [isMobile]
  );

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  useEffect(() => {
    if (!step.target) {
      setTargetRect(null);
      setShowOverlay(true);
      return;
    }
    const timer = setTimeout(() => {
      const elem = document.querySelector(step.target!);
      if (elem) {
        const rect = elem.getBoundingClientRect();
        setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
        setShowOverlay(true);
      } else {
        setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [step.target, currentStep, steps.length]);

  const getTooltipStyle = (): CSSProperties => {
    if (!targetRect || step.position === "center") {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 10001,
      };
    }
    const offset = 24;
    const tooltipWidth = 320;
    const tooltipHeight = 180;
    const positions: Record<string, CSSProperties> = {
      top: {
        top: targetRect.top - tooltipHeight - offset,
        left: Math.max(16, targetRect.left + targetRect.width / 2 - tooltipWidth / 2),
      },
      bottom: {
        top: targetRect.top + targetRect.height + offset,
        left: Math.max(16, targetRect.left + targetRect.width / 2 - tooltipWidth / 2),
      },
      left: {
        top: targetRect.top + targetRect.height / 2 - tooltipHeight / 2,
        left: targetRect.left - tooltipWidth - offset,
      },
      right: {
        top: targetRect.top + targetRect.height / 2 - tooltipHeight / 2,
        left: targetRect.left + targetRect.width + offset,
      },
    };
    return { position: "fixed", ...positions[step.position], zIndex: 10001 };
  };

  const handleNext = () => {
    if (isLastStep) {
      localStorage.setItem("lpa_onboarding_done", "true");
      onComplete?.();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const handleSkip = () => {
    localStorage.setItem("lpa_onboarding_done", "true");
    onComplete?.();
  };

  if (!showOverlay) return null;

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 9999,
          transition: TRANSITION.normal,
        }}
        onClick={() => {
          if (isLastStep) handleNext();
        }}
      />
      {targetRect && step.position !== "center" && (
        <div
          style={{
            position: "fixed",
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            border: "2px solid #CC8544",
            borderRadius: RADIUS.lg,
            boxShadow: "0 0 0 4px rgba(204, 133, 68, 0.1), 0 0 24px rgba(204, 133, 68, 0.2)",
            zIndex: 10000,
            pointerEvents: "none",
            transition: TRANSITION.normal,
          }}
        />
      )}
      <div
        ref={tooltipRef}
        style={{
          ...getTooltipStyle(),
          background: GLASS.dark.bg,
          backdropFilter: GLASS.dark.blur,
          border: `1px solid ${GLASS.dark.border}`,
          borderRadius: RADIUS.lg,
          padding: SP[6],
          boxShadow: GLASS.dark.shadow,
          color: T.text,
          fontFamily: "'Inter', sans-serif",
          animation: "onboarding-fade-in 0.3s ease-out",
          minWidth: 300,
          maxWidth: 340,
        }}
      >
        <div
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color: T.muted,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: SP[2],
          }}
        >
          Passo {currentStep + 1} de {steps.length}
        </div>
        <h3 style={{ fontSize: "16px", fontWeight: 700, margin: `0 0 ${SP[2]} 0`, color: T.text }}>
          {step.title}
        </h3>
        <p style={{ fontSize: "13px", lineHeight: 1.6, margin: `0 0 ${SP[4]} 0`, color: T.sub }}>
          {step.description}
        </p>
        {step.id === "push-notifications" && (
          <button
            onClick={async () => {
              await subscribeToPush();
              handleNext();
            }}
            style={{
              width: "100%",
              padding: `${SP[3]} ${SP[4]}`,
              background: T.accent,
              border: "none",
              borderRadius: RADIUS.md,
              color: T.accentText,
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              transition: TRANSITION.fast,
              marginBottom: SP[3],
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.opacity = "0.9";
              (e.target as HTMLElement).style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.opacity = "1";
              (e.target as HTMLElement).style.transform = "translateY(0)";
            }}
          >
            🔔 Ativar Notificações
          </button>
        )}
        <div
          style={{ display: "flex", gap: SP[2], justifyContent: "space-between", flexWrap: "wrap" }}
        >
          {currentStep > 0 && (
            <button
              onClick={handleBack}
              style={{
                padding: `${SP[2]} ${SP[4]}`,
                background: "transparent",
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.md,
                color: T.sub,
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                transition: TRANSITION.fast,
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.background = T.surface;
                (e.target as HTMLElement).style.color = T.text;
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.background = "transparent";
                (e.target as HTMLElement).style.color = T.sub;
              }}
            >
              ← Voltar
            </button>
          )}
          <button
            onClick={handleSkip}
            style={{
              padding: `${SP[2]} ${SP[4]}`,
              background: "transparent",
              border: "none",
              color: T.muted,
              fontSize: "12px",
              fontWeight: 500,
              cursor: "pointer",
              transition: TRANSITION.fast,
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.color = T.sub;
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.color = T.muted;
            }}
          >
            Pular
          </button>
          <button
            onClick={handleNext}
            style={{
              padding: `${SP[2]} ${SP[4]}`,
              background: T.accent,
              border: "none",
              borderRadius: RADIUS.md,
              color: T.accentText,
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              transition: TRANSITION.fast,
              marginLeft: "auto",
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.opacity = "0.9";
              (e.target as HTMLElement).style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.opacity = "1";
              (e.target as HTMLElement).style.transform = "translateY(0)";
            }}
          >
            {isLastStep ? "Pronto!" : "Próximo →"}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes onboarding-fade-in {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>
    </>
  );
}
